// Pure helpers for the "dots" shape of the Releases pixelation overlay: a
// halftone of round dots on a regular grid. Kept React-free and DOM-free (the
// draw function takes a 2D context) so the grid, tone and size math can be
// unit-tested directly.
//
// Each grid cell gets one dot. The tone of the cell (0–1) sets the dot area:
//   - "image": the mean luminance of the background under the cell, so bright
//     areas give large dots and dark areas give small or no dots (or the
//     reverse with `invert`).
//   - "random": seeded fractal noise, so dots of different sizes form random
//     structures. `structure` sets the size of the structures, in cells.
// The mask (libs/pixelationMask.ts) scales the tone, so a dot shrinks and goes
// away where the mask is erased.
//
// The colour of a dot is the mean colour of the image under it ("image"), or
// comes from the palette ("palette"). With the palette, the image pattern
// maps the tone to the palette sorted by luminance (a gradient map, with a
// little seeded dither), so the large dots of bright areas get the bright
// colours; the random pattern takes a seeded colour per dot, as the squares do.

import {hashInts, hexToRgb, mulberry32} from "./pixelation";

export const PIXELATION_SHAPES = ["squares", "dots"] as const;
export type PixelationShape = (typeof PIXELATION_SHAPES)[number];

export const DOT_PATTERNS = ["image", "random"] as const;
export type DotPattern = (typeof DOT_PATTERNS)[number];

export const DOT_COLORS = ["image", "palette"] as const;
export type DotColor = (typeof DOT_COLORS)[number];

/** Dots smaller than this radius (px) are not drawn. */
const MIN_RADIUS = 0.35;

export type DotGrid = {
  cols: number;
  rows: number;
  /** Cell width, px. `cols * cellW` is the full width. */
  cellW: number;
  /** Cell height, px. `rows * cellH` is the full height. */
  cellH: number;
};

/**
 * The dot grid for a (width × height) canvas and a pitch (px). The counts are
 * rounded so that a whole number of cells fills each axis: cells are nearly
 * square and no row or column is cut at the edge.
 */
export function dotGrid(width: number, height: number, pitch: number): DotGrid {
  if (!(width > 0) || !(height > 0) || !(pitch > 0)) {
    throw new Error(`dotGrid: invalid size ${width}×${height}, pitch ${pitch}`);
  }
  const cols = Math.max(1, Math.round(width / pitch));
  const rows = Math.max(1, Math.round(height / pitch));
  return {cols, rows, cellW: width / cols, cellH: height / rows};
}

/**
 * The tone curve. `tone` is −100…100: 0 keeps the tone, a positive value
 * makes all dots larger (light), a negative value makes them smaller (dark).
 */
export function applyTone(value: number, tone: number): number {
  const v = Math.min(1, Math.max(0, value));
  if (!tone) return v;
  return Math.pow(v, Math.pow(2, -tone / 50));
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function lattice(seed: number, x: number, y: number): number {
  return mulberry32(hashInts(seed, x, y))();
}

/** Seeded 2D value noise, 0–1, smooth between integer lattice points. */
export function valueNoise(seed: number, x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = smooth(x - x0);
  const ty = smooth(y - y0);
  const a = lattice(seed, x0, y0);
  const b = lattice(seed, x0 + 1, y0);
  const c = lattice(seed, x0, y0 + 1);
  const d = lattice(seed, x0 + 1, y0 + 1);
  const top = a + (b - a) * tx;
  const bottom = c + (d - c) * tx;
  return top + (bottom - top) * ty;
}

/**
 * Three octaves of value noise, stretched so that the result uses the full
 * 0–1 range: the random pattern then has empty areas and dense areas, not
 * only medium dots. `structure` is the size of the largest features, in cells
 * (1 gives a different size for almost every dot).
 */
export function structureNoise(seed: number, col: number, row: number, structure: number): number {
  const s = Math.max(0.5, structure);
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = 1 / s;
  for (let octave = 0; octave < 3; octave++) {
    // Each octave reads its own lattice, so the octaves do not line up.
    sum += amp * valueNoise((seed + octave * 7919) >>> 0, col * freq, row * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  const n = sum / norm;
  return Math.min(1, Math.max(0, (n - 0.5) * 2.2 + 0.5));
}

/** A summed-area table of a single-channel value, for O(1) area means. */
export type ToneField = {
  width: number;
  height: number;
  /** (width + 1) × (height + 1) prefix sums, row-major. */
  sums: Float64Array;
};

/**
 * Build a tone field from RGBA pixels: the luminance (Rec. 709, 0–1) of every
 * pixel, as a summed-area table. The pixels must already be composited over
 * the colour behind the image; alpha is ignored.
 */
export function toneFieldFromRgba(
  data: Uint8ClampedArray,
  width: number,
  height: number
): ToneField {
  if (data.length < width * height * 4) {
    throw new Error(`toneFieldFromRgba: ${data.length} bytes for ${width}×${height}`);
  }
  const stride = width + 1;
  const sums = new Float64Array(stride * (height + 1));
  for (let y = 0; y < height; y++) {
    let rowSum = 0;
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      rowSum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      sums[(y + 1) * stride + (x + 1)] = sums[y * stride + (x + 1)] + rowSum;
    }
  }
  return {width, height, sums};
}

/**
 * The mean tone of a rectangle in field pixels. The rectangle is clipped to
 * the field; an empty rectangle gives 0.
 */
export function meanTone(field: ToneField, x0: number, y0: number, x1: number, y1: number): number {
  const ax = Math.max(0, Math.min(field.width, Math.floor(x0)));
  const ay = Math.max(0, Math.min(field.height, Math.floor(y0)));
  const bx = Math.max(0, Math.min(field.width, Math.ceil(x1)));
  const by = Math.max(0, Math.min(field.height, Math.ceil(y1)));
  if (bx <= ax || by <= ay) return 0;
  const s = field.sums;
  const stride = field.width + 1;
  const total =
    s[by * stride + bx] - s[ay * stride + bx] - s[by * stride + ax] + s[ay * stride + ax];
  return total / ((bx - ax) * (by - ay));
}

/** RGBA pixels of the background (libs/backgroundRaster.ts). */
export type Raster = {data: Uint8ClampedArray; width: number; height: number};

/**
 * The mean colour of a rectangle in raster pixels, as "#rrggbb" with 5 bits
 * per channel. The coarse colour lets drawDots fill many dots in one path; the
 * step is too small to see. The rectangle is clipped to the raster.
 */
export function meanColor(raster: Raster, x0: number, y0: number, x1: number, y1: number): string {
  const ax = Math.max(0, Math.min(raster.width, Math.floor(x0)));
  const ay = Math.max(0, Math.min(raster.height, Math.floor(y0)));
  const bx = Math.max(ax + 1, Math.min(raster.width, Math.ceil(x1)));
  const by = Math.max(ay + 1, Math.min(raster.height, Math.ceil(y1)));
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let y = ay; y < by && y < raster.height; y++) {
    for (let x = ax; x < bx && x < raster.width; x++) {
      const i = (y * raster.width + x) * 4;
      r += raster.data[i];
      g += raster.data[i + 1];
      b += raster.data[i + 2];
      n++;
    }
  }
  if (!n) return "#000000";
  const q = (v: number) =>
    Math.min(255, (Math.round(v / n) >> 3) * 8 + 4)
      .toString(16)
      .padStart(2, "0");
  return `#${q(r)}${q(g)}${q(b)}`;
}

export type DotParams = {
  /** Grid pitch, px. */
  pitch: number;
  /** Largest dot diameter as % of the cell (100 = dots touch). */
  dotScale: number;
  pattern: DotPattern;
  /** −100…100, see applyTone. */
  tone: number;
  /** Image pattern: dark areas give large dots. */
  invert: boolean;
  /** Random pattern: feature size in cells. */
  structure: number;
  seed: number;
  /** Where the dot colours come from. */
  color: DotColor;
  /** The palette for `color: "palette"`. */
  palette: readonly string[];
};

function luminance(hex: string): number {
  const {r, g, b} = hexToRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Palette indexes sorted from the darkest colour to the brightest. */
export function paletteByLuminance(palette: readonly string[]): number[] {
  return palette
    .map((hex, i) => ({i, l: luminance(hex)}))
    .sort((a, b) => a.l - b.l || a.i - b.i)
    .map((e) => e.i);
}

export type Dot = {x: number; y: number; r: number; color: string};

type RectSampler<T> = (x0: number, y0: number, x1: number, y1: number) => T;

/** Samplers of the background under the dots, all in canvas px. */
export type DotSamplers = {
  /** Mean image tone (0–1) of a rectangle. Necessary for the image pattern. */
  toneAt: RectSampler<number> | null;
  /** Mean image colour of a rectangle. Necessary for image colours. */
  colorAt: RectSampler<string> | null;
  /** Mask value (0–1) at a point. Without it the mask is full. */
  maskAt: ((x: number, y: number) => number) | null;
};

/** Compute every visible dot. */
export function computeDots(
  width: number,
  height: number,
  params: DotParams,
  {toneAt, colorAt, maskAt}: DotSamplers
): Dot[] {
  if (params.pattern === "image" && !toneAt) {
    throw new Error("computeDots: the image pattern needs a tone sampler");
  }
  if (params.color === "image" && !colorAt) {
    throw new Error("computeDots: image colours need a colour sampler");
  }
  if (params.color === "palette" && !params.palette.length) {
    throw new Error("computeDots: the palette is empty");
  }
  const order = paletteByLuminance(params.palette);
  const grid = dotGrid(width, height, params.pitch);
  const maxR = 0.5 * Math.min(grid.cellW, grid.cellH) * (params.dotScale / 100);
  const seed = params.seed >>> 0;
  const dots: Dot[] = [];
  for (let row = 0; row < grid.rows; row++) {
    const y0 = row * grid.cellH;
    const cy = y0 + grid.cellH / 2;
    for (let col = 0; col < grid.cols; col++) {
      const x0 = col * grid.cellW;
      const cx = x0 + grid.cellW / 2;
      const mask = maskAt ? maskAt(cx, cy) : 1;
      if (mask <= 0) continue;
      let value: number;
      if (params.pattern === "image") {
        value = toneAt!(x0, y0, x0 + grid.cellW, y0 + grid.cellH);
        if (params.invert) value = 1 - value;
      } else {
        value = structureNoise(seed, col, row, params.structure);
      }
      const toned = applyTone(value, params.tone);
      // The dot AREA follows the tone, so the radius is its square root.
      const r = maxR * Math.sqrt(toned * Math.min(1, mask));
      if (r < MIN_RADIUS) continue;
      let color: string;
      if (params.color === "image") {
        color = colorAt!(x0, y0, x0 + grid.cellW, y0 + grid.cellH);
      } else if (order.length === 1) {
        color = params.palette[order[0]];
      } else {
        const pick = mulberry32(hashInts(seed ^ 0x5bd1e995, col, row))();
        // Image pattern: the tone picks the colour, the dither blurs the steps
        // between colours. Random pattern: any colour.
        const rank = params.pattern === "image" ? toned + (pick - 0.5) / order.length : pick;
        const k = Math.min(order.length - 1, Math.max(0, Math.floor(rank * order.length)));
        color = params.palette[order[k]];
      }
      dots.push({x: cx, y: cy, r, color});
    }
  }
  return dots;
}

/**
 * Draw dots onto a context. Dots of one colour go into one path, so a full
 * banner of dots is a few fills, not thousands. Does not clear the canvas.
 */
export function drawDots(
  ctx: CanvasRenderingContext2D,
  dots: readonly Dot[],
  opacity: number
): void {
  const groups = new Map<string, Dot[]>();
  for (const d of dots) {
    const group = groups.get(d.color);
    if (group) group.push(d);
    else groups.set(d.color, [d]);
  }
  ctx.save();
  ctx.globalAlpha = Math.min(1, Math.max(0, opacity / 100));
  for (const [color, group] of groups) {
    ctx.beginPath();
    for (const d of group) {
      ctx.moveTo(d.x + d.r, d.y);
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
    }
    ctx.fillStyle = color;
    ctx.fill();
  }
  ctx.restore();
}
